import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { SEO } from "../components/common/SEO";
import { api } from "../lib/api";
import { useLanguage } from "../context/LanguageProvider";
import { pick, UI_STRINGS } from "../lib/i18n";
import { Calendar } from "lucide-react";

function formatDate(iso, lang) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(
            lang === "fr" ? "fr-CA" : "en-CA",
            { year: "numeric", month: "long", day: "numeric" },
        );
    } catch {
        return "";
    }
}

export default function BlogPost() {
    const { slug } = useParams();
    const { lang } = useLanguage();
    const [state, setState] = useState({
        status: "loading",
        post: null,
        error: "",
    });

    useEffect(() => {
        let cancelled = false;
        setState({ status: "loading", post: null, error: "" });
        api.get(`/blog-posts/${slug}`)
            .then((res) => {
                if (cancelled) return;
                setState({ status: "ok", post: res.data, error: "" });
            })
            .catch((err) => {
                if (cancelled) return;
                setState({
                    status: err?.response?.status === 404 ? "not_found" : "error",
                    post: null,
                    error: err?.message || "",
                });
            });
        return () => {
            cancelled = true;
        };
    }, [slug]);

    if (state.status === "loading") {
        return (
            <div
                data-testid="blog-post-loading"
                className="mx-auto max-w-3xl px-5 py-32 text-center text-neutral-400"
            >
                {pick(UI_STRINGS.misc.loading, lang)}
            </div>
        );
    }
    if (state.status === "not_found") {
        return (
            <div
                data-testid="blog-post-notfound"
                className="mx-auto max-w-3xl px-5 py-32 text-center"
            >
                <div className="font-serif text-3xl text-neutral-50 mb-3">
                    {pick(UI_STRINGS.misc.page_not_found, lang)}
                </div>
                <Link to="/blog" className="btn-outline-gold mt-6 inline-flex">
                    {pick(UI_STRINGS.cta.back_to_blog, lang)}
                </Link>
            </div>
        );
    }

    const post = state.post;
    if (!post) return null;
    const bodyText = pick(post.body, lang) || "";
    const paragraphs = bodyText.split(/\n\s*\n/).filter(Boolean);

    return (
        <div data-testid="page-blog-post" className="bg-ink">
            <SEO
                title={`${pick(post.title, lang)} — Cabinet MABS`}
                description={pick(post.excerpt, lang)}
                lang={lang}
            />
            <article className="mx-auto max-w-3xl px-5 lg:px-8 py-16 md:py-20">
                <Link
                    to="/blog"
                    className="text-xs uppercase tracking-widest text-gold hover:text-gold-hover transition-colors"
                    data-testid="blog-post-back"
                >
                    {pick(UI_STRINGS.cta.back_to_blog, lang)}
                </Link>
                <div className="flex items-center gap-2 text-xs text-neutral-500 mt-8 mb-4">
                    <Calendar size={12} />
                    <span>{formatDate(post.created_at, lang)}</span>
                </div>
                <h1 className="font-serif text-3xl md:text-5xl leading-tight text-neutral-50 mb-6">
                    {pick(post.title, lang)}
                </h1>
                <p className="text-lg text-neutral-300 leading-relaxed border-l-2 border-gold pl-5 italic mb-10">
                    {pick(post.excerpt, lang)}
                </p>
                <div className="prose-mabs" data-testid="blog-post-body">
                    {paragraphs.map((p, i) => (
                        <p key={i}>{p}</p>
                    ))}
                </div>
            </article>
        </div>
    );
}
